import { sel } from "../selectors";
import { ROUGH_NOTE } from "../stage/fixtures/sets/polishTheDraft";
import { DEFAULT_STAGE_VIEWPORT, Scene } from "./types";

// Lesson: Writing and editing → Polish the draft.
// Fixture: `polishTheDraft` (a finished theft report with a rough note in the
// third paragraph, a name misheard throughout, and a stage-only proofreader
// that flags "recieved" and "Mercado").
const CASE_ID = "c0000000-0000-4000-8000-000000000301";
const NARRATIVE = `${sel("editorNarrative")} .ProseMirror`;
const LAST_PARAGRAPH = `${NARRATIVE} p:last-of-type`;
const ROUGH_PARAGRAPH = `${NARRATIVE} p:nth-of-type(2)`;
const MARK = `${sel("editorNarrative")} .proof-mark`;
const TYPED = " I also learned a neighbor recieved a similar report last month, according to Gil Mercado.";

const scene: Scene = {
  id: "polish-the-draft",
  title: "Polish the draft",
  startRoute: `/cases/${CASE_ID}`,
  fixture: "polishTheDraft",
  viewport: DEFAULT_STAGE_VIEWPORT,
  steps: [
    {
      id: "intro",
      narration:
        "A draft is rarely done after the first read. Three tools clean one up: Enhance Writing, writing suggestions, and Find and Replace.",
      actions: [
        { type: "waitFor", target: sel("editorNarrative") },
        { type: "moveTo", target: sel("editorNarrative") },
        { type: "wait", ms: 4500 },
      ],
    },
    {
      id: "select",
      narration:
        "This paragraph is still a rough note I typed between calls. I select it, and the floating menu appears.",
      actions: [
        { type: "moveTo", target: ROUGH_PARAGRAPH },
        { type: "spotlight", target: ROUGH_PARAGRAPH },
        { type: "wait", ms: 2600 },
        { type: "spotlight", target: null },
        { type: "selectText", target: ROUGH_PARAGRAPH, text: ROUGH_NOTE },
        { type: "waitFor", target: sel("enhanceWriting") },
        { type: "wait", ms: 1500 },
      ],
    },
    {
      id: "enhance",
      narration:
        "Click Enhance Writing, and the Original and Enhanced versions appear side by side. It only rephrases what you wrote, in the style from your Report Preferences.",
      actions: [
        { type: "moveTo", target: sel("enhanceWriting") },
        { type: "wait", ms: 300 },
        { type: "click", target: sel("enhanceWriting") },
        { type: "waitFor", target: sel("enhanceCompare") },
        { type: "wait", ms: 6500 },
      ],
    },
    {
      id: "accept",
      narration:
        "Accept Changes replaces the selection. Discard keeps your original.",
      actions: [
        // "Discard keeps your original" ends at 4.6 s; accept lands as it finishes.
        { type: "moveTo", target: sel("enhanceAccept") },
        { type: "wait", ms: 3000 },
        { type: "click", target: sel("enhanceAccept") },
        { type: "wait", ms: 600 },
      ],
    },
    {
      id: "suggestions",
      narration:
        "Next, writing suggestions. Click Writing suggestions in the toolbar to turn it on, then edit a paragraph and pause. A second later, underlines appear, but only on text you changed.",
      actions: [
        // "turned on" is said around 3.0 s; the stage starts with it off.
        { type: "moveTo", target: sel("editorProofread") },
        { type: "wait", ms: 3000 },
        { type: "click", target: sel("editorProofread") },
        { type: "wait", ms: 1500 },
        { type: "scrollIntoView", target: LAST_PARAGRAPH },
        { type: "selectText", target: LAST_PARAGRAPH, collapse: "end" },
        { type: "type", target: LAST_PARAGRAPH, text: TYPED, cps: 16 },
        { type: "waitFor", target: MARK, timeoutMs: 6000 },
        { type: "wait", ms: 600 },
      ],
    },
    {
      id: "accept-suggestion",
      narration:
        "Click an underline to see the suggested replacement. Click received, and the word is fixed.",
      actions: [
        { type: "moveTo", target: MARK },
        { type: "wait", ms: 400 },
        { type: "click", target: MARK, pointer: true },
        { type: "waitFor", target: sel("proofPopover") },
        { type: "wait", ms: 1800 },
        { type: "click", target: sel("proofApply") },
        { type: "wait", ms: 1000 },
      ],
    },
    {
      id: "dictionary",
      narration:
        "For a name it keeps flagging, click Add to dictionary. That adds it to Common Spellings in your Report Preferences, so future drafts and checks use it.",
      actions: [
        { type: "moveTo", target: MARK },
        { type: "wait", ms: 300 },
        { type: "click", target: MARK, pointer: true },
        { type: "waitFor", target: sel("proofDictionary") },
        { type: "wait", ms: 1400 },
        { type: "click", target: sel("proofDictionary") },
        { type: "wait", ms: 1800 },
      ],
    },
    {
      id: "find",
      narration:
        "Finally, Find and Replace. The transcript misheard Vasquez as Vasques, and it appears all through the report. Open it from the toolbar and type the wrong spelling, and every match highlights.",
      actions: [
        // "Open it from the toolbar" starts at 8.0 s of the clip.
        { type: "moveTo", target: sel("editorFind") },
        { type: "wait", ms: 6500 },
        { type: "click", target: sel("editorFind") },
        { type: "waitFor", target: `${sel("findBar")} input` },
        { type: "wait", ms: 600 },
        { type: "type", target: `${sel("findBar")} input`, text: "Vasques", cps: 7 },
        { type: "wait", ms: 2500 },
      ],
    },
    {
      id: "replace",
      narration:
        "Click the replace button to add the replacement field, type the right spelling, and click Replace All. Search matches inside words, so use the full name when you can.",
      actions: [
        { type: "moveTo", target: sel("findReplaceToggle") },
        { type: "wait", ms: 500 },
        { type: "click", target: sel("findReplaceToggle") },
        { type: "waitFor", target: `${sel("findBar")} input[placeholder^="Replace"]` },
        { type: "wait", ms: 800 },
        { type: "type", target: `${sel("findBar")} input[placeholder^="Replace"]`, text: "Vasquez", cps: 7 },
        { type: "wait", ms: 1000 },
        { type: "moveTo", target: sel("replaceAll") },
        { type: "wait", ms: 900 },
        { type: "click", target: sel("replaceAll") },
        { type: "wait", ms: 1500 },
      ],
    },
    {
      id: "outro",
      narration:
        "One last habit. Before you export, search for an open bracket to land on every placeholder still in the report, and fill each one.",
      actions: [
        { type: "moveTo", target: `${sel("findBar")} input` },
        { type: "spotlight", target: sel("findBar") },
        { type: "wait", ms: 4500 },
        { type: "moveTo", target: sel("editorExport") },
        { type: "spotlight", target: sel("editorExport") },
        { type: "wait", ms: 2000 },
        { type: "spotlight", target: null },
      ],
    },
  ],
};

export default scene;
